import { useState, useContext, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  CircularProgress,
  alpha,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DrawIcon from '@mui/icons-material/Draw';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';

interface Delivery {
  id: string;
  status: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  deliveryAddress: {
    street: string;
    city: string;
    state?: string;
    postalCode?: string;
    country?: string;
    notes?: string;
  };
  notes?: string;
  driverNotes?: string;
  adminNotes?: string;
  failureReason?: string;
  proofOfDelivery?: Array<{
    type: 'signature' | 'photo';
    filePath: string;
    fileName: string;
    uploadedBy: string;
    uploadedAt: string;
  }>;
}

interface DeliveryStopManageDialogProps {
  open: boolean;
  onClose: () => void;
  delivery: Delivery | null;
  stopNumber: number;
  onStatusUpdated?: () => void;
}

const updateDeliveryStatus = async ({
  id,
  status,
  adminNotes,
  failureReason,
}: {
  id: string;
  status: string;
  adminNotes?: string;
  failureReason?: string;
}): Promise<Delivery> => {
  const response = await axiosInstance.patch(`/deliveries/${id}`, {
    status,
    adminNotes,
    failureReason,
  });
  return response.data;
};

const uploadProofOfDelivery = async ({
  deliveryId,
  file,
  type,
}: {
  deliveryId: string;
  file: File;
  type: 'photo' | 'signature';
}): Promise<Delivery> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);

  const response = await axiosInstance.post(
    `/deliveries/${deliveryId}/proof-of-delivery`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

export const DeliveryStopManageDialog = ({
  open,
  onClose,
  delivery,
  stopNumber,
  onStatusUpdated,
}: DeliveryStopManageDialogProps) => {
  const { mode } = useContext(ColorModeContext);
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const [newStatus, setNewStatus] = useState<string>('');
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [failureReason, setFailureReason] = useState<string>('');
  const [confirmChange, setConfirmChange] = useState(false);
  const [uploadType, setUploadType] = useState<'photo' | 'signature'>('photo');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Reset state when dialog opens with new delivery
  const handleEnter = () => {
    setNewStatus(delivery?.status || '');
    setAdminNotes(delivery?.adminNotes || '');
    setFailureReason(delivery?.failureReason || '');
    setConfirmChange(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const statusMutation = useMutation({
    mutationFn: updateDeliveryStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      onStatusUpdated?.();
      onClose();
      showMessage('Delivery status updated', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update delivery status', 'error');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: uploadProofOfDelivery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      onStatusUpdated?.();
      setSelectedFile(null);
      setPreviewUrl(null);
      showMessage('Proof of delivery uploaded successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to upload proof of delivery', 'error');
    },
  });

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    []
  );

  const handleUpload = () => {
    if (selectedFile && delivery) {
      uploadMutation.mutate({
        deliveryId: delivery.id,
        file: selectedFile,
        type: uploadType,
      });
    }
  };

  const handleSaveStatus = () => {
    if (delivery && newStatus && newStatus !== delivery.status) {
      statusMutation.mutate({
        id: delivery.id,
        status: newStatus,
        adminNotes: adminNotes || undefined,
        failureReason: newStatus === 'failed' ? failureReason : undefined,
      });
    }
  };

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'pending':
        return palette.textMuted;
      case 'assigned':
        return palette.info;
      case 'picked_up':
        return palette.warning;
      case 'out_for_delivery':
        return palette.primary;
      case 'delivered':
        return palette.success;
      case 'failed':
        return palette.error;
      case 'cancelled':
        return palette.textMuted;
      default:
        return palette.textMuted;
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (!delivery) return null;

  const hasStatusChange = newStatus && newStatus !== delivery.status;
  const canSave = hasStatusChange && confirmChange;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.border}`,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: alpha(palette.primary, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.bold,
              color: palette.primary,
            }}
          >
            {stopNumber}
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              Manage Delivery Stop
            </Typography>
            <Chip
              label={delivery.status.replace(/_/g, ' ').toUpperCase()}
              size="small"
              sx={{
                backgroundColor: alpha(getStatusColor(delivery.status), 0.1),
                color: getStatusColor(delivery.status),
                fontFamily: typography.fontFamily.mono,
                fontSize: '10px',
                fontWeight: typography.fontWeight.bold,
                mt: 0.5,
              }}
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.textMuted }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {/* Delivery Info */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <LocationOnIcon sx={{ color: palette.info, mt: 0.5 }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {delivery.deliveryAddress?.street}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                {delivery.deliveryAddress?.city}
                {delivery.deliveryAddress?.state && `, ${delivery.deliveryAddress.state}`}
                {delivery.deliveryAddress?.postalCode && ` ${delivery.deliveryAddress.postalCode}`}
              </Typography>
            </Box>
          </Box>
          {delivery.customerName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon sx={{ color: palette.primary }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {delivery.customerName}
                </Typography>
                {delivery.customerPhone && (
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    {delivery.customerPhone}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Status Update Section */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
                color: colors.textMuted,
              }}
            >
              Update Status
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => setNewStatus(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.border,
                  },
                }}
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(opt.value),
                        }}
                      />
                      {opt.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {newStatus === 'failed' && (
              <TextField
                fullWidth
                size="small"
                label="Failure Reason"
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            )}

            <TextField
              fullWidth
              size="small"
              label="Admin Notes"
              placeholder="Explain why you're overriding this delivery status..."
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />

            {hasStatusChange && (
              <Alert
                severity="warning"
                icon={newStatus === 'delivered' ? <CheckCircleIcon /> : <ErrorIcon />}
                sx={{
                  mb: 2,
                  backgroundColor: alpha(
                    newStatus === 'delivered' ? palette.success : palette.warning,
                    0.1
                  ),
                  border: `1px solid ${alpha(
                    newStatus === 'delivered' ? palette.success : palette.warning,
                    0.3
                  )}`,
                }}
              >
                <Typography variant="body2">
                  You are about to change the status from{' '}
                  <strong>{delivery.status.replace(/_/g, ' ')}</strong> to{' '}
                  <strong>{newStatus.replace(/_/g, ' ')}</strong>
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmChange}
                      onChange={(e) => setConfirmChange(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="caption">
                      I confirm this action
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />
              </Alert>
            )}
          </Grid>

          {/* Proof of Delivery Section */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
                color: colors.textMuted,
              }}
            >
              Proof of Delivery
            </Typography>

            {/* Existing Proofs */}
            {delivery.proofOfDelivery && delivery.proofOfDelivery.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
                  Existing files ({delivery.proofOfDelivery.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {delivery.proofOfDelivery.map((proof, idx) => (
                    <Chip
                      key={idx}
                      icon={proof.type === 'photo' ? <PhotoCameraIcon /> : <DrawIcon />}
                      label={proof.fileName || `${proof.type} ${idx + 1}`}
                      size="small"
                      sx={{
                        backgroundColor: alpha(palette.success, 0.1),
                        color: palette.success,
                        '& .MuiChip-icon': { color: palette.success },
                      }}
                      onClick={() => {
                        // Open image in new tab
                        window.open(`http://localhost:3001${proof.filePath}`, '_blank');
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Upload New Proof */}
            <Box
              sx={{
                p: 2,
                border: `2px dashed ${colors.border}`,
                borderRadius: theme.radius.xs,
                backgroundColor: alpha(colors.surfaceElevated, 0.5),
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant={uploadType === 'photo' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => setUploadType('photo')}
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '11px',
                  }}
                >
                  Photo
                </Button>
                <Button
                  variant={uploadType === 'signature' ? 'contained' : 'outlined'}
                  size="small"
                  startIcon={<DrawIcon />}
                  onClick={() => setUploadType('signature')}
                  sx={{
                    fontFamily: typography.fontFamily.mono,
                    fontSize: '11px',
                  }}
                >
                  Signature
                </Button>
              </Box>

              {previewUrl ? (
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: 150,
                      objectFit: 'contain',
                      borderRadius: 4,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl(null);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: alpha(palette.error, 0.9),
                      color: '#fff',
                      '&:hover': { backgroundColor: palette.error },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  component="label"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 3,
                    cursor: 'pointer',
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    Click to upload {uploadType}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    JPEG, PNG (max 10MB)
                  </Typography>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    onChange={handleFileSelect}
                  />
                </Box>
              )}

              {selectedFile && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={
                    uploadMutation.isPending ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                  sx={{
                    backgroundColor: palette.primary,
                    fontFamily: typography.fontFamily.mono,
                  }}
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
        <Button
          onClick={onClose}
          sx={{
            color: colors.textMuted,
            fontFamily: typography.fontFamily.mono,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveStatus}
          disabled={!canSave || statusMutation.isPending}
          startIcon={
            statusMutation.isPending ? (
              <CircularProgress size={16} />
            ) : newStatus === 'delivered' ? (
              <CheckCircleIcon />
            ) : (
              undefined
            )
          }
          sx={{
            backgroundColor: newStatus === 'delivered' ? palette.success : palette.primary,
            fontFamily: typography.fontFamily.mono,
            fontWeight: typography.fontWeight.bold,
            '&:hover': {
              backgroundColor: alpha(
                newStatus === 'delivered' ? palette.success : palette.primary,
                0.9
              ),
            },
          }}
        >
          {statusMutation.isPending
            ? 'Saving...'
            : `Mark as ${newStatus?.replace(/_/g, ' ') || 'Updated'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryStopManageDialog;
